// EM²c ACTIVE Building Registry
// Locked 2026-08-19 after the building-system cleanup pass.
// Removed/legacy definitions are preserved in old-buildings-and-logic.js and
// backups/pre-building-overhaul/. This file contains PLAYER-ACTIVE buildings only.
//
// Asset-system invariants:
// - fixed human scale; growth is modular, never model scaling
// - snap/grid based; shared connector language
// - function-first, reusable, expansion-ready

export const EM2C_CATEGORIES = [
  { id: "core", label: "Command & Communications" },
  { id: "habitation", label: "Habitation" },
  { id: "medical", label: "Medical & Safety" },
  { id: "life", label: "Life Support & Food" },
  { id: "power", label: "Power" },
  { id: "mining", label: "Mining & Materials" },
  { id: "manufacturing", label: "Manufacturing" },
  { id: "storage", label: "Logistics" },
  { id: "drones", label: "Drones & Vehicles" },
  { id: "spaceport", label: "Spaceport" },
  { id: "research", label: "Science & Research" },
  { id: "gas", label: "Gas & Propellant" },
];

const P1 = [0];
const P2 = [0, 3];
const P4 = [0, 1, 3, 4];
const P6 = [0, 1, 2, 3, 4, 5];

const F1 = [{ q: 0, r: 0 }];
const F2 = [{ q: 0, r: 0 }, { q: 1, r: 0 }];
const F3 = [{ q: 0, r: 0 }, { q: 1, r: 0 }, { q: 0, r: 1 }];
const F5 = [{ q: 0, r: 0 }, { q: 1, r: 0 }, { q: -1, r: 0 }, { q: 0, r: 1 }, { q: 0, r: -1 }];
const F7 = [{ q: 0, r: 0 }, { q: 1, r: 0 }, { q: -1, r: 0 }, { q: 0, r: 1 }, { q: 0, r: -1 }, { q: 1, r: -1 }, { q: -1, r: 1 }];

const base = (id, name, category, overrides = {}) => ({
  id,
  name,
  category,
  tier: 0,
  description: "",
  footprintHexes: F1,
  entranceEdges: [3],
  corridorPorts: [],
  powerPorts: P1,
  waterPorts: [],
  oxygenPorts: [],
  dataPorts: P1,
  vehicleAccess: [3],
  cost: {},
  buildTime: 10,
  workersRequired: 0,
  maintenanceCost: {},
  powerConsumption: 0,
  waterConsumption: 0,
  oxygenConsumption: 0,
  heatProduction: 0,
  productionInputs: {},
  productionOutputs: {},
  storageCapacity: {},
  unlockRequirement: { tier: overrides.tier ?? 0 },
  locked: false,
  color: "#3a3f4b",
  accent: "#00e5ff",
  size: [4, 2.5, 4],
  shape: "clean",
  ...overrides,
});

export const EM2C_BUILDINGS = {
  // ───────────────── COMMAND & COMMUNICATIONS ─────────────────
  command_center: base("command_center", "Command Center", "core", {
    description: "Primary colony control root. Day-0 command and drone construction coverage.",
    cost: {}, buildTime: 0, footprintHexes: F1, corridorPorts: P2, dataPorts: P4,
    powerConsumption: 2, size: [4, 3, 4], shape: "clean",
    networkRole: "command", controlRadius: 6, dayZeroUnlocked: true,
  }),
  mega_command: base("mega_command", "Command Complex", "core", {
    tier: 4, locked: true,
    description: "Late-stage modular command complex for city-scale colony control.",
    footprintHexes: F7, corridorPorts: P4, powerPorts: P4, waterPorts: P2, oxygenPorts: P2, dataPorts: P6,
    cost: { refined_metal: 160, machine_parts: 70, polymers: 50, credits: 400 }, buildTime: 100,
    workersRequired: 6, powerConsumption: 10, size: [12, 4, 12],
    networkRole: "command", controlRadius: 10,
  }),
  relay_tower: base("relay_tower", "Relay Tower", "core", {
    description: "Day-0 control relay. Must be placed inside the existing active command network and extends drone/build coverage.",
    cost: { refined_metal: 10, machine_parts: 4 }, buildTime: 10, powerConsumption: 1,
    dataPorts: P4, size: [3, 5, 3], shape: "slim",
    networkRole: "relay", controlRadius: 8, dayZeroUnlocked: true,
  }),
  sat_uplink: base("sat_uplink", "Satellite Uplink Station", "core", {
    tier: 2, locked: true,
    description: "Links the colony command network to Earth-orbit communication assets.",
    cost: { refined_metal: 15, machine_parts: 6 }, buildTime: 14, powerConsumption: 2, dataPorts: P2,
    size: [4, 4, 4], shape: "slim",
  }),
  long_range_array: base("long_range_array", "Long-Range Communications Array", "core", {
    tier: 3, locked: true,
    description: "Deep-space communications for future solar-system missions.",
    footprintHexes: F3, cost: { refined_metal: 30, machine_parts: 15 }, buildTime: 30,
    powerConsumption: 4, dataPorts: P2, size: [8, 5, 6], shape: "slim",
  }),

  // ───────────────── HABITATION ─────────────────
  habitat_module: base("habitat_module", "Standard Habitat Module", "habitation", {
    description: "Main modular colony housing. Fixed-size units connect to expand capacity.",
    footprintHexes: F2, corridorPorts: P2, waterPorts: P1, oxygenPorts: P1,
    cost: { refined_metal: 15, machine_parts: 6, polymers: 4 }, buildTime: 20,
    powerConsumption: 3, residentCapacity: 6, size: [5, 2.5, 4], shape: "sealed",
  }),
  habitat_complex: base("habitat_complex", "Large Habitat Complex", "habitation", {
    tier: 3, locked: true,
    description: "Established-colony housing assembled from repeated fixed-size habitat modules.",
    footprintHexes: F5, corridorPorts: P4, powerPorts: P2, waterPorts: P2, oxygenPorts: P2, dataPorts: P2,
    cost: { refined_metal: 60, machine_parts: 20, polymers: 15, credits: 100 }, buildTime: 50,
    powerConsumption: 10, residentCapacity: 24, size: [10, 3, 10], shape: "sealed",
    modularGrowth: true,
  }),
  crew_quarters: base("crew_quarters", "Crew Quarters", "habitation", {
    tier: 1,
    description: "Compact housing for workers at remote mining, research and construction sites.",
    corridorPorts: P2, waterPorts: P1, oxygenPorts: P1,
    cost: { refined_metal: 10, machine_parts: 4 }, buildTime: 14, powerConsumption: 2,
    residentCapacity: 4, size: [4, 2.5, 4], shape: "sealed",
  }),
  community_center: base("community_center", "Community Center", "habitation", {
    tier: 2, locked: true,
    description: "Small gym, café, restaurant and social hub. Later research upgrades it into the Recreation Center / Mars mall social complex.",
    footprintHexes: F3, corridorPorts: P2, waterPorts: P1, oxygenPorts: P1,
    cost: { refined_metal: 30, machine_parts: 10, polymers: 8 }, buildTime: 30,
    workersRequired: 2, powerConsumption: 4, productionOutputs: { morale: 3 }, size: [8, 3, 6], shape: "sealed",
    upgradeName: "Recreation Center", upgradeResearch: "bio_health1", modularGrowth: true,
  }),

  // ───────────────── MEDICAL & SAFETY ─────────────────
  medical_center: base("medical_center", "Medical Center", "medical", {
    tier: 1,
    description: "Routine treatment, injuries and diagnostics.", footprintHexes: F3, corridorPorts: P2,
    waterPorts: P1, oxygenPorts: P1, cost: { refined_metal: 12, machine_parts: 6, polymers: 3 },
    buildTime: 18, workersRequired: 2, powerConsumption: 4, productionOutputs: { health: 0.05 },
    size: [8, 2.5, 6], shape: "clean",
  }),
  medical_complex: base("medical_complex", "Medical Complex", "medical", {
    tier: 3, locked: true,
    description: "Major hospital with trauma, surgery, advanced diagnostics and integrated quarantine/isolation modules.",
    footprintHexes: F5, corridorPorts: P4, powerPorts: P2, waterPorts: P2, oxygenPorts: P2,
    cost: { refined_metal: 70, machine_parts: 28, polymers: 16 }, buildTime: 60,
    workersRequired: 6, powerConsumption: 9, productionOutputs: { health: 0.12 }, size: [10, 4, 10], shape: "clean",
  }),
  emergency_response: base("emergency_response", "Emergency Response Station", "medical", {
    tier: 1,
    description: "Fire, accident, rescue and hazardous-incident response.",
    cost: { refined_metal: 15, machine_parts: 6 }, buildTime: 12, workersRequired: 2,
    powerConsumption: 2, waterPorts: P1, oxygenPorts: P1, vehicleAccess: [0, 3], shape: "boxy",
  }),
  shelter_bunker: base("shelter_bunker", "Emergency Bunker", "medical", {
    tier: 1,
    description: "Storm/disaster shelter with emergency food, Clean Water and oxygen reserves; suitable for remote sites.",
    corridorPorts: P2, waterPorts: P1, oxygenPorts: P2,
    cost: { refined_metal: 20, machine_parts: 5 }, buildTime: 18, powerConsumption: 1,
    storageCapacity: { food: 30, water: 30, oxygen: 30 }, shape: "heavy",
  }),

  // ───────────────── LIFE SUPPORT & FOOD ─────────────────
  water_extractor: base("water_extractor", "Regolith Water Extractor", "life", {
    description: "Extracts Raw Water from surveyed Hydrated Mineral Zones; no ice stage exists.",
    cost: { refined_metal: 10, machine_parts: 4 }, buildTime: 14, powerConsumption: 4,
    productionOutputs: { raw_water: 5 }, storageCapacity: { raw_water: 30 }, requiresDeposit: "hydrated_minerals",
    depositLabel: "Hydrated Mineral Zone", shape: "mechanical",
  }),
  atmospheric_water_harvester: base("atmospheric_water_harvester", "Atmospheric Water Harvester", "life", {
    description: "Low-output, high-power emergency Raw Water source from trace atmospheric moisture.",
    cost: { refined_metal: 10, machine_parts: 4, polymers: 2 }, buildTime: 14, powerConsumption: 10,
    productionOutputs: { raw_water: 0.8 }, storageCapacity: { raw_water: 20 }, shape: "mechanical",
  }),
  water_purifier: base("water_purifier", "Water Purification Plant", "life", {
    tier: 1,
    description: "Converts Raw Water into Clean Water.", footprintHexes: F2, waterPorts: P2,
    cost: { refined_metal: 15, machine_parts: 6 }, buildTime: 16, powerConsumption: 2,
    productionInputs: { raw_water: 4 }, productionOutputs: { water: 5 }, storageCapacity: { water: 50 }, shape: "mechanical",
  }),
  oxygen_gen: base("oxygen_gen", "Oxygen Generator", "life", {
    description: "Electrolyses Clean Water into oxygen and hydrogen. Both outputs must be handled by the colony.",
    cost: { refined_metal: 12, machine_parts: 5 }, buildTime: 16, powerConsumption: 3,
    productionInputs: { water: 2 }, productionOutputs: { oxygen: 4, hydrogen: 1 },
    storageCapacity: { oxygen: 30, hydrogen: 20 }, oxygenPorts: P2, waterPorts: P1, shape: "mechanical",
  }),
  greenhouse: base("greenhouse", "Greenhouse", "life", {
    tier: 1,
    description: "Early crop production under controlled light and atmosphere.", footprintHexes: F3, corridorPorts: P2,
    waterPorts: P1, oxygenPorts: P1, cost: { refined_metal: 10, machine_parts: 4, polymers: 2 },
    buildTime: 16, powerConsumption: 3, productionInputs: { water: 2 }, productionOutputs: { food: 3 },
    storageCapacity: { food: 30 }, size: [8, 2.5, 6], shape: "sealed",
  }),
  hydroponics: base("hydroponics", "Hydroponics Facility", "life", {
    tier: 2,
    description: "Higher-efficiency controlled crop production using recirculating nutrient water.", footprintHexes: F2,
    waterPorts: P2, oxygenPorts: P1, cost: { refined_metal: 20, machine_parts: 8, polymers: 4 },
    buildTime: 20, powerConsumption: 5, productionInputs: { water: 3 }, productionOutputs: { food: 5 },
    storageCapacity: { food: 50 }, size: [6, 3, 4], shape: "sealed",
  }),
  agri_complex: base("agri_complex", "Large Agricultural Complex", "life", {
    tier: 3, locked: true,
    description: "High-capacity modular agriculture for established colonies.", footprintHexes: F5,
    powerPorts: P2, waterPorts: P2, oxygenPorts: P1, cost: { refined_metal: 40, machine_parts: 15, polymers: 10 },
    buildTime: 40, powerConsumption: 8, productionInputs: { water: 6 }, productionOutputs: { food: 10 },
    storageCapacity: { food: 100 }, size: [12, 3, 12], shape: "sealed", modularGrowth: true,
  }),
  food_processing: base("food_processing", "Food Processing Facility", "life", {
    tier: 3, locked: true,
    description: "Turns agricultural output into preserved colony-ready food.", footprintHexes: F2,
    waterPorts: P1, oxygenPorts: P1, cost: { refined_metal: 25, machine_parts: 10 }, buildTime: 25,
    workersRequired: 3, powerConsumption: 4, productionInputs: { food: 4 }, productionOutputs: { food: 5 },
    storageCapacity: { food: 80 }, size: [6, 2.5, 4], shape: "industrial",
  }),
  atmo_processor: base("atmo_processor", "Atmospheric Processing Plant", "life", {
    tier: 2,
    description: "Separates useful gases from the Martian atmosphere for the colony gas system.", footprintHexes: F3,
    cost: { refined_metal: 30, machine_parts: 12 }, buildTime: 30, powerConsumption: 6,
    productionOutputs: { co2: 4, nitrogen: 0.12, argon: 0.08 }, storageCapacity: { co2: 30, nitrogen: 10, argon: 10 },
    size: [8, 3, 6], shape: "industrial",
  }),
  waste_recycler: base("waste_recycler", "Waste Recycling Plant", "life", {
    tier: 1,
    description: "Closed-loop recovery plant for colony waste, biomass and demolition salvage.", footprintHexes: F2,
    cost: { refined_metal: 15, machine_parts: 6 }, buildTime: 18, powerConsumption: 2,
    productionInputs: { recyclable_material: 2 }, productionOutputs: { construction_material: 1, methane: 0.4, co2: 0.2 },
    size: [6, 2.5, 4], shape: "industrial",
  }),

  // ───────────────── POWER ─────────────────
  solar_station: base("solar_station", "Solar Power Station", "power", {
    description: "Daylight power generation; dust storms reduce output.", cost: { refined_metal: 6, machine_parts: 2 },
    buildTime: 8, productionOutputs: { power: 12 }, size: [4, 0.5, 4], shape: "flat",
  }),
  power_reactor: base("power_reactor", "Power Reactor", "power", {
    tier: 2,
    description: "Heavy stable reactor for non-solar base load.", footprintHexes: F2, powerPorts: P2, waterPorts: P1,
    cost: { refined_metal: 40, machine_parts: 20, polymers: 5 }, buildTime: 40,
    productionOutputs: { power: 30 }, size: [6, 3, 4], shape: "heavy",
  }),
  reactor_complex: base("reactor_complex", "Reactor Complex", "power", {
    tier: 3, locked: true,
    description: "Multi-reactor modular complex for industrial colony power.", footprintHexes: F5,
    powerPorts: P4, waterPorts: P2, cost: { refined_metal: 80, machine_parts: 40, polymers: 10, credits: 200 },
    buildTime: 80, productionOutputs: { power: 80 }, size: [10, 4, 10], shape: "heavy", modularGrowth: true,
  }),
  battery_storage: base("battery_storage", "Battery Storage Facility", "power", {
    description: "Research-upgraded L1-L5 battery. Existing facilities upgrade automatically.",
    cost: { refined_metal: 8, machine_parts: 3 }, buildTime: 10, powerPorts: P2,
    storageCapacity: { power: 200 }, size: [3, 1.5, 3], shape: "boxy",
    upgradeLevels: 5, automaticUpgrade: true,
    levelCapacities: { 1: 200, 2: 300, 3: 450, 4: 650, 5: 900 },
  }),
  power_distributor: base("power_distributor", "Power Distribution Station", "power", {
    description: "Distributes generated power across the colony utility grid.", cost: { refined_metal: 2 },
    buildTime: 3, powerPorts: P4, size: [2, 1, 2], shape: "slim",
  }),

  // ───────────────── MINING & MATERIALS ─────────────────
  mining_outpost: base("mining_outpost", "Mining Outpost", "mining", {
    tier: 1,
    description: "Extracts Metal Ore directly from surveyed metal deposits.",
    cost: { refined_metal: 8, machine_parts: 3 }, buildTime: 12, powerConsumption: 2,
    productionOutputs: { metal_ore: 4 }, storageCapacity: { metal_ore: 30 }, requiresDeposit: "metal",
    vehicleAccess: [0, 3], shape: "mechanical",
  }),
  ore_refinery: base("ore_refinery", "Ore Refinery", "mining", {
    tier: 2,
    description: "Separates Metal Ore into useful metals; refinery residue is retained for further processing.", footprintHexes: F5,
    cost: { machine_parts: 12, polymers: 10 }, buildTime: 30, powerConsumption: 5,
    productionInputs: { metal_ore: 4 },
    productionOutputs: { iron: 2, aluminum: 0.6, copper: 0.3, silicon: 0.4, refinery_tailings: 1, carbon_monoxide: 0.2 },
    storageCapacity: { iron: 40, aluminum: 20, copper: 15, silicon: 15, refinery_tailings: 30, carbon_monoxide: 15 },
    vehicleAccess: [0, 3], size: [10, 3, 10], shape: "industrial",
  }),
  regolith_processor: base("regolith_processor", "Regolith Processing Plant", "mining", {
    tier: 2,
    description: "Processes Martian regolith and refinery residue into foundation and hard-structure building material.", footprintHexes: F3,
    cost: { machine_parts: 8, structural_components: 4 }, buildTime: 24, powerConsumption: 4,
    productionInputs: { regolith: 4 }, productionOutputs: { construction_material: 3 },
    storageCapacity: { construction_material: 60 }, vehicleAccess: [0, 3], size: [8, 3, 6], shape: "industrial",
  }),

  // ───────────────── MANUFACTURING ─────────────────
  factory_complex: base("factory_complex", "Factory Complex", "manufacturing", {
    tier: 2,
    description: "Single modular manufacturing system for Machine Parts, Electronics, Utility Components and Structural Components. Additive manufacturing is a research capability, not a separate building.",
    footprintHexes: F5, cost: { refined_metal: 14, polymers: 6 }, buildTime: 28,
    workersRequired: 3, powerConsumption: 6, storageCapacity: { machine_parts: 30, electronic_components: 20, utility_components: 20, structural_components: 30 },
    vehicleAccess: [0, 3], size: [10, 3, 10], shape: "industrial", recipeBuilding: true,
  }),
  vehicle_assembly: base("vehicle_assembly", "Vehicle Assembly Facility", "manufacturing", {
    tier: 3, locked: true,
    description: "Builds drones, rovers and colony vehicles from locally manufactured components.", footprintHexes: F3,
    cost: { refined_metal: 35, machine_parts: 15 }, buildTime: 35, workersRequired: 3, powerConsumption: 5,
    productionInputs: { machine_parts: 3, electronic_components: 2, structural_components: 2 },
    vehicleAccess: [0, 3], size: [8, 3, 6], shape: "open",
  }),
  repair_workshop: base("repair_workshop", "Repair & Maintenance Workshop", "manufacturing", {
    tier: 1,
    description: "Repairs buildings, drones, rovers and vehicles using locally made replacement parts.", footprintHexes: F2,
    cost: { refined_metal: 12, machine_parts: 5 }, buildTime: 14, workersRequired: 2, powerConsumption: 3,
    productionInputs: { machine_parts: 0.5 }, size: [6, 2.5, 4], shape: "industrial",
  }),

  // ───────────────── LOGISTICS ─────────────────
  small_storage: base("small_storage", "General Storage Module", "storage", {
    description: "Early-colony and general-purpose mixed storage. Later remains useful as a flexible colony supply module.",
    cost: { refined_metal: 8, machine_parts: 2 }, buildTime: 10, shape: "boxy",
    storageCapacity: {
      food: 80, water: 60, oxygen: 60, refined_metal: 80, iron: 80, aluminum: 40, copper: 30, silicon: 30,
      construction_material: 80, machine_parts: 50, electronic_components: 30, utility_components: 30,
      structural_components: 50, polymers: 40, processed_minerals: 40, fertilizer: 30,
      recyclable_material: 50, medical_supplies: 20, mission_supplies: 30,
    },
  }),
  remote_supply: base("remote_supply", "Remote Supply Module", "storage", {
    tier: 1,
    description: "Compact remote-camp supply module carrying only essential survival, maintenance and construction stocks.",
    cost: { refined_metal: 6, machine_parts: 2 }, buildTime: 8, shape: "boxy",
    storageCapacity: { food: 40, water: 40, oxygen: 40, machine_parts: 25, construction_material: 30, medical_supplies: 15 },
  }),
  raw_storage_yard: base("raw_storage_yard", "Raw Material Storage Yard", "storage", {
    tier: 1,
    description: "Bulk storage for Metal Ore, regolith and refinery residue.", footprintHexes: F2,
    cost: { refined_metal: 5, machine_parts: 2 }, buildTime: 6, shape: "open",
    storageCapacity: { metal_ore: 250, regolith: 250, refinery_tailings: 200 },
    buildMenuParent: "mining_outpost", nestedStorage: true,
  }),
  water_tank: base("water_tank", "Raw Water Storage Tank", "storage", {
    description: "Dedicated fixed-size Raw Water buffer tank.", cost: { refined_metal: 8, polymers: 3 },
    buildTime: 10, waterPorts: P2, storageCapacity: { raw_water: 150 }, shape: "cylindrical",
    buildMenuParent: "water_extractor", nestedStorage: true,
  }),
  clean_water_reservoir: base("clean_water_reservoir", "Large Clean Water Reservoir", "storage", {
    tier: 1,
    description: "Three linked fixed-size tank modules; 450 Clean Water capacity without scaling tank size.",
    footprintHexes: F3, cost: { refined_metal: 24, polymers: 9 }, buildTime: 24, waterPorts: P4,
    storageCapacity: { water: 450 }, size: [8, 3, 6], shape: "cylindrical", modularGrowth: true,
    buildMenuParent: "water_purifier", nestedStorage: true,
  }),
  oxygen_tank: base("oxygen_tank", "Oxygen Storage Tank", "storage", {
    description: "Dedicated compressed-oxygen storage.", cost: { refined_metal: 8, polymers: 3 },
    buildTime: 10, oxygenPorts: P2, storageCapacity: { oxygen: 150 }, shape: "cylindrical",
    buildMenuParent: "oxygen_gen", nestedStorage: true,
  }),
  food_storage: base("food_storage", "Cold Food Storage", "storage", {
    description: "Powered refrigerated food storage.", cost: { refined_metal: 6, polymers: 4 },
    buildTime: 10, powerConsumption: 1, storageCapacity: { food: 250 }, shape: "boxy",
    buildMenuParent: "greenhouse", nestedStorage: true,
  }),
  logistics_hub: base("logistics_hub", "Automated Logistics Hub", "storage", {
    tier: 2, locked: true,
    description: "Automatically routes resources between producers, storage and consumers.", footprintHexes: F3,
    cost: { refined_metal: 25, machine_parts: 12 }, buildTime: 25, powerConsumption: 3,
    dataPorts: P4, vehicleAccess: [0, 3], size: [8, 2.5, 6], shape: "boxy",
  }),

  // ───────────────── DRONES & VEHICLES ─────────────────
  drone_bay: base("drone_bay", "Drone Bay", "drones", {
    description: "Stores, charges and deploys construction/logistics drones.", footprintHexes: F2,
    cost: { refined_metal: 10, machine_parts: 4 }, buildTime: 12, powerConsumption: 2,
    powerPorts: P2, dataPorts: P2, vehicleAccess: [0, 3], size: [6, 2.5, 4], shape: "boxy",
  }),
  rover_garage: base("rover_garage", "Rover Garage", "drones", {
    tier: 1,
    description: "Stores and deploys rovers for exploration and remote operations.", footprintHexes: F2,
    cost: { refined_metal: 15, machine_parts: 6 }, buildTime: 16, powerConsumption: 2,
    vehicleAccess: [0, 3], size: [6, 2.5, 4], shape: "boxy",
  }),
  charging_station: base("charging_station", "Charging & Refuelling Station", "drones", {
    tier: 1,
    description: "Field support point for drones and rovers at remote camps.",
    cost: { refined_metal: 8, machine_parts: 3 }, buildTime: 8, powerPorts: P2,
    storageCapacity: { power: 50 }, vehicleAccess: [0, 3], size: [3, 1.5, 3], shape: "slim",
  }),

  // ───────────────── SPACEPORT ─────────────────
  landing_pad: base("landing_pad", "Landing Pad", "spaceport", {
    description: "Single standard pad for cargo and colonist rockets.", cost: { refined_metal: 20, machine_parts: 6 },
    buildTime: 22, powerConsumption: 1, vehicleAccess: [0, 3], size: [6, 0.5, 6], shape: "flat",
  }),
  spaceport_terminal: base("spaceport_terminal", "Spaceport Terminal", "spaceport", {
    tier: 3, locked: true,
    description: "Later-stage passenger and cargo handling hub for the colony spaceport.", footprintHexes: F3,
    corridorPorts: P2, waterPorts: P1, oxygenPorts: P1, dataPorts: P2,
    cost: { refined_metal: 40, machine_parts: 15, polymers: 5 }, buildTime: 40, workersRequired: 4,
    powerConsumption: 5, size: [8, 3, 6], shape: "clean", vehicleAccess: [0, 3],
  }),

  // ───────────────── SCIENCE & RESEARCH ─────────────────
  research_lab: base("research_lab", "Research Lab", "research", {
    description: "Starting research facility. Later remains useful at remote camps and field sites.", footprintHexes: F2,
    corridorPorts: P2, waterPorts: P1, oxygenPorts: P1, dataPorts: P2,
    cost: { refined_metal: 20, machine_parts: 10, polymers: 5 }, buildTime: 25, workersRequired: 3,
    powerConsumption: 4, productionOutputs: { research_points: 2 }, size: [6, 3, 4], shape: "clean",
  }),
  mars_science: base("mars_science", "Research Complex", "research", {
    tier: 3, locked: true,
    description: "Main-colony advanced research complex unlocked after the starting research stage.", footprintHexes: F5,
    corridorPorts: P2, powerPorts: P2, waterPorts: P1, oxygenPorts: P1, dataPorts: P4,
    cost: { refined_metal: 50, machine_parts: 20, polymers: 10 }, buildTime: 50, workersRequired: 6,
    powerConsumption: 8, productionOutputs: { research_points: 5 }, size: [10, 3, 10], shape: "clean",
  }),

  // ───────────────── GAS & PROPELLANT ─────────────────
  gas_refinery: base("gas_refinery", "Gas Refinery", "gas", {
    tier: 2, locked: true,
    description: "Purifies and converts colony gas streams into reusable Hydrogen, Methane, CO₂, Nitrogen and Argon. CO is treated as an internal refinery feed, not normal storage.",
    footprintHexes: F3, cost: { refined_metal: 30, machine_parts: 14, electronic_components: 4 }, buildTime: 32,
    powerConsumption: 7, storageCapacity: { hydrogen: 20, methane: 20, co2: 20, nitrogen: 10, argon: 10 },
    size: [8, 3, 6], shape: "industrial", recipeBuilding: true,
    upgradeLevels: 3, modularGrowth: true,
  }),
  gas_storage: base("gas_storage", "Gas Storage Tank Plant", "gas", {
    tier: 1,
    description: "Modular gas tank plant. Oxygen is intentionally excluded because Oxygen Storage Tanks handle O₂ separately.",
    footprintHexes: F5, cost: { refined_metal: 28, machine_parts: 8 }, buildTime: 28, shape: "cylindrical",
    storageCapacity: { hydrogen: 300, methane: 300, co2: 300, nitrogen: 300, argon: 300 },
    upgradeLevels: 3, modularGrowth: true,
    levelCapacities: {
      1: { hydrogen: 300, methane: 300, co2: 300, nitrogen: 300, argon: 300 },
      2: { hydrogen: 750, methane: 750, co2: 750, nitrogen: 750, argon: 750 },
      3: { hydrogen: 1500, methane: 1500, co2: 1500, nitrogen: 1500, argon: 1500 },
    },
    size: [10, 3, 10],
  }),
  propellant_refinery: base("propellant_refinery", "Propellant Refinery", "gas", {
    tier: 3, locked: true,
    description: "Takes Methane from the gas system and Oxygen from dedicated O₂ storage to produce rocket propellant.",
    footprintHexes: F3, cost: { refined_metal: 35, machine_parts: 16, electronic_components: 5 }, buildTime: 36,
    powerConsumption: 8, size: [8, 3, 6], shape: "industrial", recipeBuilding: true,
    upgradeLevels: 3, modularGrowth: true,
  }),
  propellant_tank_farm: base("propellant_tank_farm", "Propellant Tank Farm", "gas", {
    tier: 3, locked: true,
    description: "Dedicated modular storage for rocket propellant produced on Mars.", footprintHexes: F5,
    cost: { refined_metal: 30, machine_parts: 10 }, buildTime: 30, shape: "cylindrical",
    storageCapacity: { rocket_propellant: 300 }, upgradeLevels: 3, modularGrowth: true,
    levelCapacities: { 1: { rocket_propellant: 300 }, 2: { rocket_propellant: 750 }, 3: { rocket_propellant: 1500 } },
    size: [10, 3, 10],
  }),
};

export const EM2C_BUILDING_LIST = Object.values(EM2C_BUILDINGS);
export const ACTIVE_BUILDING_IDS = Object.freeze(EM2C_BUILDING_LIST.map((building) => building.id));
